/* istanbul ignore next */
define('logger', [
	'uuid',
	'utils/util',
	'performance',
	'Promise',
	'Emitter'
], function (
	/** uuid */uuid,
	/** util */util,
	/** performance */performance,
	/** Promise */Promise,
	/** Emitter */Emitter
) {
	'use strict';

	var R_AUTO_LEVELS = /\b(fail|error|warn(ing)?|info|success|done)\b/i;
	var AUTO_LEVELS_COLORS = {
		fail: '#c00',
		error: '#c00',
		warn: '#a6a616',
		warning: '#a6a616',
		info: '#00f',
		success: '#060',
		done: '#060'
	};

	Error.prototype.toJSON = function () {
		return {
			name: this.name,
			message: this.message,
			stack: this.stack
		};
	};

	/**
	 * @typedef   {Object}  LoggerMeta
	 * @property  {string}  fn     название метода
	 * @property  {strong}  file   путь к файлу
	 * @property  {number}  line
	 * @property  {number}  column
	 */


	var gid = 1,
		getListeners = Emitter.getListeners,
		encodeURIComponent = window.encodeURIComponent,

		RSPACE = /\s+/,

		index = {},
		entries = []
	;

	function _getPromiseName(entry, added) {
		return '[[' + entry.label.replace(/(:pending|^\[\[|\]\]$)/g, '') + ':' + added + ']]';
	}


	/**
	 * Глобальный объект логирования
	 * @class logger
	 * @constructs logger
	 */
	var logger = /** @lends logger */{
		index: index,
		parentId: 0,
		metaOffset: 0,


		/**
		 * Статус работы логера
		 * @type {boolean}
		 * @methodOf logger
		 */
		disabled: false,


		/**
		 * Массив записей
		 * @type {logger.Entry[]}
		 * @methodOf logger
		 */
		entries: entries,


		/**
		 * Использовать UUID идетификатор в качестве id записи
		 * @type {boolean}
		 * @methodOf logger
		 */
		useUUID: false,


		/**
		 * Генерация UUID
		 * @name logger.uuid
		 * @method
		 * @static
		 * @returns  {string}
		 * @methodOf logger
		 */
		uuid: uuid,


		/**
		 * Перехват ошибок с `window`
		 * @param  {Window}  window
		 * @methodOf logger
		 */
		catchUncaughtException: function (window) {
			window.onerror = function windowOnError(message, url, line, column, err) {
				logger.add('error', {
					type: err && err.type,
					message: message,
					url: url,
					line: line,
					column: column,
					stack: err && err.stack.toString()
				});
				return false;
			};
		},


		/**
		 * Добавить лог-запись
		 * @param  {string}  label  метка
		 * @param  {*}       args   аргументы для логирования
		 * @param  {*}       [parentId]
		 * @param  {LoggerMeta} [meta]
		 * @return {logger.Entry}
		 * @methodOf logger
		 */
		add: function (label, args, parentId, meta) {
			if (logger.disabled) {
				return this;
			}

			logger.metaOffset = 1;
			return this.addEntry('log', label, args, parentId, meta);
		},


		/**
		 * Функция фитрации аргументов
		 * @param  {*}  args
		 * @return {*}
		 * @methodOf logger
		 */
		filterArguments: function (args) {
			return args;
		},


		/**
		 * Создать scope
		 * @param  {string} label
		 * @param  {*} args
		 * @param  {LoggerMeta|number} [meta]  числом можно передать смещение (metaOffset) по stack
		 * @param  {number} [parentId]
		 * @return {logger.Entry}
		 * @methodOf logger
		 */
		scope: function (label, args, meta, parentId) {
			/* jshint eqnull:true */
			if (meta == null) {
				meta = -2;
			}

			/* istanbul ignore next */
			if (meta >= -99) {
				meta = logger.meta(meta);
			}

			logger.metaOffset = 1;
			return this.addEntry('scope', label, args, parentId, meta);
		},


		/**
		 * Добавить запись
		 * @param   {string|Entry}  type        тип записи (scope, log)
		 * @param   {string}        [label]     метка
		 * @param   {*}             [args]      аргументы
		 * @param   {*}             [parentId]  id родителя
		 * @param   {LoggerMeta}    [meta]
		 * @return  {logger.Entry}
		 * @methodOf logger
		 */
		addEntry: function (type, label, args, parentId, meta) {
			logger.metaOffset += 1;

			var entry = type instanceof Entry ? type : new Entry(type, label, args, parentId || this.parentId, meta);

			index[entry.id] = entry;
			entry.idx = entries.push(entry) - 1;

			this._emit();

			return entry;
		},


		/**
		 * Получить «родительскую» запись
		 * @param   {*} id
		 * @param   {string} label
		 * @returns {*}
		 * @methodOf logger
		 */
		closest: function (id, label) {
			var entry;

			id = id.id || id;

			while ((entry = index[id]) && id) {
				id = entry.parentId;

				if (entry.label === label) {
					return entry;
				}
			}

			return null;
		},


		/**
		 * Получить лог-цепочу по id записи
		 * @param   {number}  id
		 * @param   {string}  [glue]
		 * @returns {Array}
		 * @methodOf logger
		 */
		chain: function (id, glue) {
			var log = [], entry;

			while ((entry = index[id]) && id) {
				id = entry.parentId;
				log.push(entry);
			}

			log.reverse();

			if (glue) {
				log = log.map(function (entry) {
					return '[' + entry.id + '] ' + entry.label;
				}).join(glue);
			}

			return log;
		},


		/**
		 * Получить лог-scope
		 * @param   {string}  name   scope-название
		 * @param   {*}       args   аргументы
		 * @param   {*}       [parentId]
		 * @param   {LoggerMeta}  [meta]
		 * @returns {Object}
		 * @methodOf logger
		 */
		getScope: function (name, args, parentId, meta) {
			logger.metaOffset += 1;
			return typeof name === 'string' ? this.addEntry('scope', name, args, parentId, meta) : name;
		},


		/**
		 * Обернуть фунцию или Promise в лог-scope
		 * @param   {string}     name  scope-название
		 * @param   {*}          args  метод или аргументы
		 * @param   {Function}   [fn]  метод
		 * @param   {number}     [parentId]
		 * @param   {LoggerMeta} [meta]
		 * @returns {Function|Promise}
		 * @methodOf logger
		 */
		wrap: function (name, args, fn, parentId, meta) {
			if (arguments.length === 1) {
				fn = name;
				name = index[logger.parentId];
			}
			else if (fn === void 0) {
				fn = args;
				args = void 0;
			}

			if (fn && !fn.__logger__ && !this.disabled) {
				logger.metaOffset += 1;

				meta = meta || logger.meta();

				if (typeof fn === 'function') {
					var wrapper = function () {
						var entry = logger.getScope(name, args, parentId, meta),
							_parentId = logger.parentId,
							retVal;

						logger.parentId = entry.id;
						logger.metaOffset += 1;

						retVal = logger.wrap(entry, fn.apply(this, arguments));

						logger.parentId = _parentId;

						return retVal;
					};

					fn.__logger__ = true;
					wrapper.__logger__ = true;

					return wrapper;
				}
				else if (fn.then) {
					logger.getScope(name, args, null, meta).wrap(fn);
				}
			}

			logger.metaOffset = 0;

			return fn;
		},


		/**
		 * Выполнить метод в лог-конексте
		 * @param   {string} label
		 * @param   {*} args
		 * @param   {Function} fn
		 * @returns {*}
		 * @methodOf logger
		 */
		call: function (label, args, fn) {
			logger.metaOffset += 1;
			return this.wrap(label, args, fn)();
		},


		/**
		 * Сбросить лог
		 * @methodOf logger
		 */
		reset: function () {
			gid = 1;

			this.index = index = {};
			this.entries = entries = [];
			this.parentId = 0;
		},


		/**
		 * Вывод лога в консоль
		 * @param   {boolean} [ret]  вернуть лог строкой
		 * @returns {string|undefined}
		 * @methodOf logger
		 */
		print: /* istanbul ignore next */function (ret) {
			var groups = {},
				retLog = '';


			entries.forEach(function (entry) {
				var id = entry.parentId;
				(groups[id] = groups[id] || []).push(entry);
			});

			function _time(ms) {
				return new Date(ms).toISOString().slice(11, -1);
			}

			(function _print(id, indent) {
				var entries = groups[id] || [],
					i = 0,
					n = entries.length,
					entry,
					log,
					args
				;

				for (; i < n; i++) {
					entry = entries[i];
					args = entry.args;
					log = ('[' + _time(entry.ts) + '] ' + entry.label);

					if (ret) {
						log += args ? ': ' + JSON.stringify(args) : '';
					}

					/* istanbul ignore else */
					if (groups[entry.id]) {
						/* istanbul ignore else */
						if (ret) {
							retLog += indent + log + '\n';
							_print(entry.id, '  ' + indent);
						}
						else {
							if (R_AUTO_LEVELS.test(log)) {
								var styles = 'color: ' + AUTO_LEVELS_COLORS[RegExp.lastMatch.toLowerCase()];
								console.group('%c' + log, styles);
								args && console.log('%c[details] ', styles, args);
							} else {
								console.group(log);
								args && console.log('[details] ', args);
							}

							_print(entry.id);
							console.groupEnd();
						}
					}
					else if (ret) {
						retLog += indent + log + '\n';
					}
					else if (R_AUTO_LEVELS.test(log)) {
						console.log('%c' + log, 'color: ' + AUTO_LEVELS_COLORS[RegExp.lastMatch.toLowerCase()], args);
					}
					else {
						console.log(log, args);
					}
				}
			})(0, '');


			return ret && retLog;
		},


		/**
		 * Подписаться на добавление записи
		 * @param   {string}   labels
		 * @param   {Function} fn
		 * @returns {logger}
		 * @methodOf logger
		 */
		on: function (labels, fn) {
			labels = labels.trim().split(RSPACE);

			var i = labels.length, label;

			while (i--) {
				label = labels[i].split(':');
				getListeners(this, label[1] || label[0]).push({
					fn: fn,
					label: label[1] || label[0],
					parent: label[1] && label[0]
				});
			}

			return this;
		},


		/**
		 * Отписаться от добавление записи
		 * @param   {string}   labels
		 * @param   {Function} fn
		 * @returns {logger}
		 * @methodOf logger
		 */
		off: function (labels, fn) {
			labels = labels.trim().split(RSPACE);

			var i = labels.length, label, list, j, handle;

			while (i--) {
				label = labels[i].split(':');
				list = getListeners(this, label[1] || label[0]);
				j = list.length;

				while (j--) {
					handle = list[j];

					if (handle.fn === fn && (handle.parent === (label[1] && label[0]))) {
						list.splice(j, 1);
					}
				}
			}

			return this;
		},


		/**
		 * Испускаем события
		 * @private
		 * @methodOf logger
		 */
		_emit: function () {
			var entry,
				parentEntry,
				list,
				e = entries.length,
				i,
				handle
			;

			while (e--) {
				entry = entries[e];

				if (!entry.emitted) {
					entry.emitted = true;
					list = getListeners(this, entry.label);
					i = list.length;

					while (i--) {
						handle = list[i];

						if (handle.parent) {
							parentEntry = this.closest(entry.id, handle.parent);

							/* istanbul ignore else */
							if (parentEntry) {
								handle.fn(parentEntry, entry);
							}
						}
						else {
							handle.fn(entry);
						}
					}
				}
			}
		},


		last: function () {
			return entries[entries.length - 1];
		},

		/**
		 * Получить текущую мета информацию
		 * @param  {number} [offset]
		 * @return {LoggerMeta}
		 * @methodOf logger
		 */
		meta: function () {
			return {fn: 'unknown', file: 'unknown', line: 0, column: 0};
		},

		/**
		 * Разобрать строчку из стека
		 * @return {LoggerMeta}
		 * @methodOf logger
		 */
		parseStackRow: function (value) {
			value += '';

			var match = value.match(/at\s+([^\s]+)(?:.*?)\(((?:http|file|\/)[^)]+:\d+)\)/),
				file;

			/* istanbul ignore next */
			if (!match) {
				match = value.match(/at\s+(.+)/);

				if (!match) {
					match = value.match(/^(.*?)(?:\/<)*@(.*?)$/) || value.match(/^()(https?:\/\/.+)/);
				} else {
					match[0] = '<anonymous>';
					match.unshift('');
				}
			}

			/* istanbul ignore next */
			if (match) {
				file = match[2].match(/^(.*?):(\d+)(?::(\d+))?$/) || [];
				match = {
					fn: (match[1] || '<anonymous>').trim(),
					file: file[1],
					line: file[2]|0,
					column: file[3]|0
				};
			}
			else {
				match = null;
			}

			return match;
		},


		/**
		 * Сериализовать лог
		 * @return {string}
		 * @methodOf logger
		 */
		serialize: function () {
			return JSON.stringify(entries);
		},


		/**
		 * Отправить на почту
		 * @param {string} email
		 * @methodOf logger
		 */
		mailTo: /* istanbul ignore next */ function (email) {
			var el = document.createElement('a');
			el.href = 'mailto:' + email
					+ '?subject=' + encodeURIComponent('[logger] ' + new Date())
					+ '&body=' + encodeURIComponent(this.serialize());
			el.innerHTML = 'send';
			el.click();
		}
	};


	/**
	 * Описание записи лога
	 * @class  logger.Entry
	 * @methodOf logger
	 * @param  {string}  type
	 * @param  {string}  [label]
	 * @param  {*}       [args]
	 * @param  {*}       [parentId]
	 */
	function Entry(type, label, args, parentId, meta) {
		/**
		 * Идетификатор записи
		 * @member {*} logger.Entry#id
		 */
		this.id = (args && args.uuid) || /* istanbul ignore next */(logger.useUUID ? uuid() : gid++);


		/**
		 * Относительное время добавления
		 * @member {number} logger.Entry#ts
		 */
		this.ts = performance.now();


		/**
		 * Тип записи
		 * @member {string} logger.Entry#type
		 */
		this.type = type;


		/**
		 * Метка записи
		 * @member {string} logger.Entry#label
		 */
		this.label = label + '';


		try {
			args = util.cloneJSON(logger.filterArguments(args));
		} catch (err) {
			args = err;
		}


		/**
		 * Аргумерты логирования
		 * @member {*} logger.Entry#args
		 */
		this.args = args;


		/**
		 * Id родительской записи
		 * @member {*} logger.Entry#parentId
		 */
		this.parentId = parentId;


		/**
		 * Список id связанных записей
		 * @member {Array} logger.Entry#linked
		 */
		this.linked = [];


		/**
		 * Мета информация (строка, файл, метод и т.п.)
		 * @type {LoggerMeta}
		 */
		logger.metaOffset += 1;
		this.meta = meta || logger.meta();
		logger.metaOffset = 0;
	}


	Entry.fn = Entry.prototype = /** @lends logger.Entry.prototype */ {
		constructor: Entry,


		/**
		 * Добавить связанную запись
		 * @param  {string}  label
		 * @param  {*}       [args]
		 * @return {logger.Entry}
		 */
		add: function (label, args) {
			return logger.add(label, args, this.id, this.meta);
		},


		/**
		 * Выполнить метод в лог-конексте
		 * @param  {string|Function}   label
		 * @param  {*}        args
		 * @param  {Function} fn
		 * @return {*}
		 */
		call: function (label, args, fn) {
			if (typeof label === 'function') {
				var parentId = logger.parentId,
					retVal;

				logger.parentId = this.id;
				retVal = this.wrap(args ? label.apply(null, args) : label());
				logger.parentId = parentId;

				return retVal;
			}

			return logger.wrap(label, args, fn, this.id, this.meta)();
		},


		wrap: function (obj) {
			if (typeof obj === 'function') {
				return logger.wrap(this, obj);
			}
			else if (obj && !obj.__logger__ && obj.then) {
				var then = obj.then,
					done = obj.done,
					fail = obj.fail,
					always = obj.always,
					logScope = this;


				obj.__logger__ = logScope;


				/* jshint expr:true */
				done && (obj.done = function (callback) {
					var noLog = obj.__noLog,
						meta = logger.meta(-1 - (obj.metaOffset|0));

					return done.call(obj, function () {
						(noLog
							? obj.__logger__
							: logger.addEntry('scope', _getPromiseName(logScope, 'done'), null, obj.__logger__.id, meta)
						).call(callback, arguments);
					});
				});


				/* jshint expr:true */
				fail && (obj.fail = function (callback) {
					var noLog = obj.__noLog,
						meta = logger.meta(-1 - (obj.metaOffset|0));

					return fail.call(obj, function () {
						(noLog
							? obj.__logger__
							: logger.addEntry('scope', _getPromiseName(logScope, 'fail'), null, obj.__logger__.id, meta)
						).call(callback, arguments);
					});
				});


				/* jshint expr:true */
				always && (obj.always = function (callback) {
					var meta = logger.meta(-1 - (obj.metaOffset|0)),
						retVal;

					obj.__noLog = true;

					retVal = always.call(obj, function () {
						logger
							.addEntry('scope', _getPromiseName(logScope, 'always'), null, obj.__logger__.id, meta)
								.call(callback, arguments);
					});

					obj.__noLog = false;

					return retVal;
				});


				obj['catch'] = function (callback) {
					return obj.then(null, callback, true);
				};


				obj.then = function (onResolved, onRejected, isCatch) {
					if (obj.__noLog) {
						return then.call(obj, onResolved, onRejected);
					}

					var meta = logger.meta(-1 - (obj.metaOffset|0) - !!isCatch),
						label = isCatch ? 'catch' : 'then',
						scope = new Entry('scope', '', null, logScope.id, meta);

					function resolved() {
						scope.label = _getPromiseName(logScope, label + ':resolved');
						logger.addEntry(scope);

						return scope.call(onResolved, arguments);
					}

					function rejected() {
						scope.label = _getPromiseName(logScope, label + ':rejected');
						logger.addEntry(scope);

						return scope.call(onRejected, arguments);
					}

					return scope.wrap(then.call(obj, onResolved ? resolved : null, onRejected ? rejected : null));
				};
			}

			return obj;
		},


		/**
		 * Создать связанный scope
		 * @param  {string} label
		 * @param  {*} [args]
		 * @param  {*} [meta]
		 */
		scope: function (label, args, meta) {
			return logger.scope(label, args, meta || -3, this.id);
		},


		/**
		 * Получить цепочку записей
		 * @param  {string} [glue]
		 * @returns {Array|string}
		 */
		chain: function (glue) {
			return logger.chain(this.id, glue);
		},


		pluck: function (key) {
			return this.chain().map(function (entry) {
				return entry[key];
			});
		},


		/**
		 * Получить родителя
		 * @return {logger.Entry}
		 */
		parent: function () {
			return index[this.parentId];
		},


		/**
		 * Получить JSON-объект
		 * @returns {Object}
		 */
		toJSON: function () {
			return {
				id: this.id,
				dependId: this.dependId,
				linked: this.linked,
				type: this.type,
				label: this.label,
				args: this.args,
				meta: this.meta
			};
		}
	};


	Function.prototype.logger = function (label, args) {
		return logger.wrap(label, args, this, null, logger.meta(-1));
	};


	// Export Entry
	logger.Entry = Entry;


	/**
	 * Логируемый setTimeout
	 * @param  {string}    name
	 * @param  {function}  callback
	 * @param  {number}    ms
	 */
	/* istanbul ignore next */
	window.setTimeoutLog = function (name, callback, ms) {};


	/**
	 * Логируемый setIntervalLog
	 * @param  {string}    name
	 * @param  {function}  callback
	 * @param  {number}    ms
	 */
	/* istanbul ignore next */
	window.setIntervalLog = function (name, callback, ms) {};


	//['setTimeout', 'setInterval'].forEach(function (name) {
	//	var method = window[name];
	//
	//	window[name + 'Log'] = function (label, callback, ms) {
	//		if (typeof callback !== 'function') {
	//			ms = callback;
	//			callback = label;
	//			label = name;
	//		}
	//
	//		var scope = logger.scope('~' + label);
	//		return method(function () {
	//			scope.call(callback);
	//		}, ms);
	//	};
	//});



	Promise.Log = function (name, executor) {
		if (executor) {
			name = '[[' + name + ']]';
		}
		else {
			executor = name;
			name = '[[promise]]';
		}

		return logger.scope(name, null, -2).call(function () {
			return new Promise(executor);
		});
	};


	// Export
	logger.version = '0.8.0';

	/* jshint boss:true */
	return (window.octolog = window.logger = logger);
});
